import React, { useEffect, useRef, useState } from 'react';
import './courseKit.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

/**
 * Fade-and-rise on scroll.
 *
 * Content must NEVER be left permanently invisible. Animation is a nicety;
 * being readable is not. So there are three separate guarantees:
 *   1. Reduced motion or no IntersectionObserver renders visible immediately.
 *   2. The observer reveals on scroll with a generous rootMargin.
 *   3. A failsafe timer reveals anything the observer never reported, which
 *      covers full-page screenshots, print, and any browser where the observer
 *      does not fire for far off-screen content.
 */
const REVEAL_FAILSAFE_MS = 2500;

/**
 * `instant` renders visible from the first paint with no fade. Use it for
 * above-the-fold hero content: a scroll reveal there only shows the visitor an
 * empty gap while the page loads (Rob, 2026-10-02).
 */
const Reveal = ({
  children,
  delay = 0,
  as: Tag = 'div',
  className = '',
  instant = false,
  ...rest
}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(() => instant || prefersReducedMotion());

  useEffect(() => {
    if (shown) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }

    const node = ref.current;
    if (!node) {
      setShown(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px 120px 0px' },
    );
    observer.observe(node);

    // Failsafe: never leave content hidden.
    const failsafe = setTimeout(() => {
      setShown(true);
      observer.disconnect();
    }, REVEAL_FAILSAFE_MS);

    return () => {
      clearTimeout(failsafe);
      observer.disconnect();
    };
  }, [shown]);

  return (
    <Tag
      ref={ref}
      className={`ck-reveal ${shown ? 'is-visible' : ''} ${className}`.trim()}
      style={delay && !instant ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
